import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { getErrorMessage } from '@/lib/api-error';
import { EMPTY_FORM_ERRORS, fieldError, formErrorsFromApiError } from '@/lib/form-errors';
import type { Campaign } from '@/types/campaigns';
import { describeCampaignIds, gastoAcumuladoLabel } from './campaigns-format';
import {
  CAMPAIGN_FORM_FIELDS,
  gastoFormFrom,
  gastoPayload,
  type GastoFormState,
} from './campaigns-payload';
import { validateGastoForm } from './campaigns-validation';
import { useUpdateCampaign } from './use-campaigns';

/**
 * «Actualizar el gasto»: la escritura frecuente de esta pantalla.
 *
 * El contrato lo dice explícitamente (`PATCH .../campaigns/:campaignId`): es la vía para mantener
 * `gastoActual` al día mientras no exista el sync con la Meta Ads API. Por eso tiene su propio
 * diálogo de un solo campo —un `PATCH` con un solo campo, sin tocar nombre, ids ni moneda— en vez
 * de obligar a abrir el formulario completo para cambiar un número.
 */

export interface GastoDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  /** Fila a la que se le actualiza el gasto (`null` = el diálogo no se pinta). */
  campaign: Campaign | null;
}

export function GastoDialog({ open, onClose, tenantId, campaign }: GastoDialogProps) {
  const updateCampaign = useUpdateCampaign(tenantId);

  const [form, setForm] = useState<GastoFormState>({ gastoActual: '' });
  const [errors, setErrors] = useState(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca con el gasto que la fila trae hoy. */
  useEffect(() => {
    if (!open || !campaign) return;

    setForm(gastoFormFrom(campaign));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, campaign]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!campaign) return;

    const nextErrors = validateGastoForm(form);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await updateCampaign.mutateAsync({ campaignId: campaign.id, payload: gastoPayload(form) });
      onClose();
    } catch (error) {
      setErrors(formErrorsFromApiError(error, CAMPAIGN_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Actualizar el gasto acumulado"
      description={campaign ? campaign.nombre : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateCampaign.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="gasto-form" loading={updateCampaign.isPending}>
            Guardar el gasto
          </Button>
        </>
      }
    >
      <form id="gasto-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        {campaign ? (
          <div className="rounded-panel border border-line bg-surface-muted px-3.5 py-3">
            <p className="text-caption text-ink-soft">{gastoAcumuladoLabel(campaign)}</p>
            <p className="mt-0.5 text-micro text-ink-faint">{describeCampaignIds(campaign)}</p>
          </div>
        ) : null}

        <Field
          label="Gasto acumulado"
          htmlFor="gastoActual"
          required
          hint="Es el total del periodo, no lo que se gastó hoy: escribe el número que reemplaza al anterior."
          error={fieldError(errors, 'gastoActual')}
        >
          <Input
            id="gastoActual"
            name="gastoActual"
            inputMode="decimal"
            placeholder="0"
            autoFocus
            value={form.gastoActual}
            invalid={Boolean(fieldError(errors, 'gastoActual'))}
            onChange={(event) => setForm({ gastoActual: event.target.value })}
          />
        </Field>

        <Alert tone="info">
          El CPA del reporte se calcula con este número. Si el anuncio ya terminó, desactiva la fila
          en vez de dejarla en 0: deja de contar sin perder el histórico.
        </Alert>
      </form>
    </Modal>
  );
}
